import { Fund, ProposalFund } from "creso-core/types";
import { imageAssetUrl } from "@/utils/url";
import {
  ProposalFormSchema,
  useProposalBuilderContext,
} from "@planning/providers/proposal-provider";
import { Button, MultiStepFormV2Config } from "web-design-system";
import z from "zod";
import { AmcLogoGroup } from "web-core";
import { useState } from "react";
import { handleError } from "@/utils/error";
import { useSaveProposal } from "./hooks/use-save-proposal";
import { useSaveProposalFunds } from "./hooks/use-save-proposal-funds";
import { BasicInfo } from "./components/proposals/proposal-basic-info";
import { ProposalSetup } from "./components/proposals/proposal-setup";
import { FundSelection } from "./components/proposals/fund-selection";
import { CustomizeRemarks } from "./components/proposals/customise-remarks";
import { DownloadProposal } from "./components/proposals/proposal-status";

export type ProposalFormValues = z.infer<typeof ProposalFormSchema>;

export const GoalIllustration: Record<
  "dark" | "light",
  Record<string, string>
> = {
  light: {
    CAR: imageAssetUrl("proposals/goal_car.webp"),
    HOUSE: imageAssetUrl("proposals/goal_house.webp"),
    MARRIAGE: imageAssetUrl("proposals/goal_wedding.webp"),
    CHILD_EDU: imageAssetUrl("proposals/goal_edu.webp"),
    RETIREMENT: imageAssetUrl("proposals/goal_retirement.webp"),
    CUSTOM: imageAssetUrl("proposals/goal_custom.webp"),
  },
  dark: {
    CAR: imageAssetUrl("proposals/goal_car_dark.webp"),
    HOUSE: imageAssetUrl("proposals/goal_house_dark.webp"),
    MARRIAGE: imageAssetUrl("proposals/goal_wedding_dark.webp"),
    CHILD_EDU: imageAssetUrl("proposals/goal_edu_dark.webp"),
    RETIREMENT: imageAssetUrl("proposals/goal_retirement_dark.webp"),
    CUSTOM: imageAssetUrl("proposals/goal_custom_dark.webp"),
  },
};

export function currencyStepper(val: number, dir?: "inc" | "dec") {
  if (dir === "inc") {
    if (val < 10_000) {
      return 1_000;
    }
    if (val < 1_00_000) {
      return 10_000;
    }

    if (val < 10_00_000) {
      return 1_00_000;
    }

    if (val < 1_00_00_000) {
      return 10_00_000;
    }

    return 1_00_00_000;
  }
  if (val > 1_00_00_000) {
    return 1_00_00_000;
  }
  if (val > 10_00_000) {
    return 10_00_000;
  }
  if (val > 1_00_000) {
    return 1_00_000;
  }
  if (val > 10_000) {
    return 10_000;
  }
  return 1_000;
}

export function durationStepper(v: number, dir?: "inc" | "dec") {
  const years = Math.floor(Number(v));
  const months = Math.round((Number(v) % 1) * 12);
  if (dir === "inc") {
    if (months < 11) {
      const nextVal = years + (months + 1) / 12;
      return nextVal - v;
    }
    return years + 1 - v;
  }
  if (months > 1) {
    const nextVal = years + (months - 1) / 12;
    return v - nextVal;
  }
  if (months === 0) {
    const nextVal = years - 1 + 11 / 12;
    return v - nextVal;
  }
  return v - years;
}

export function durationDisplayFormat(v: number | string) {
  const years = Math.floor(Number(v));
  const months = Math.round((Number(v) % 1) * 12);
  let display = "";
  if (years > 0) display += `${years} ${years === 1 ? "yr " : "yrs "}`;
  if (months > 0) display += `${months} ${months === 1 ? "mo" : "mos"}`;
  return display;
}

export function formatDuration(durationInMonths: number) {
  const durationYears = Math.floor(durationInMonths / 12);
  const durationMonths = durationInMonths % 12;
  return `${durationYears > 0 ? `${durationYears} year${durationYears !== 1 ? "s" : ""}` : ""} ${durationMonths > 0 ? `${durationMonths} month${durationMonths !== 1 ? "s" : ""}` : ""}`;
}

function isFundAllocationComplete(proposal: ProposalFormValues) {
  const isConfigured =
    Number(proposal.total_lumpsum_amount) > 0 ||
    Number(proposal.total_sip_amount) > 0;
  if (!isConfigured) return false;
  const lumpsumDone =
    Number(proposal.total_lumpsum_amount) === 0 ||
    Math.round(
      (proposal.proposal_funds
        .filter(
          (f) => f.type === "LUMPSUM" && !Number.isNaN(Number(f.lumpsum_amount))
        )
        .reduce((sum, f) => sum + Number(f.lumpsum_amount), 0) /
        Number(proposal.total_lumpsum_amount)) *
        100
    ) === 100;
  const sipDone =
    Number(proposal.total_sip_amount) === 0 ||
    Math.round(
      (proposal.proposal_funds
        .filter((f) => f.type === "SIP" && !Number.isNaN(Number(f.sip_amount)))
        .reduce((sum, f) => sum + Number(f.sip_amount), 0) /
        Number(proposal.total_sip_amount)) *
        100
    ) === 100;
  return lumpsumDone && sipDone;
}

function BacktestButton({ proposal }: { proposal: ProposalFormValues }) {
  const { goTo } = useProposalBuilderContext();
  const saveProposal = useSaveProposal();
  const saveProposalFunds = useSaveProposalFunds();
  const [inProgress, setInProgress] = useState(false);

  const enabled = isFundAllocationComplete(proposal);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setInProgress(true);
      // Sidebar navigation can reach this step without going through the
      // footer's save-on-continue, so the saved proposal record's amounts
      // and fund allocations can both be stale here.
      await Promise.all([saveProposal(), saveProposalFunds()]);
      goTo("backtest");
    } catch (error) {
      handleError(error, "Failed to save funds");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Button
      disabled={!enabled || inProgress}
      variant="outline"
      className="mt-2"
      onClick={handleClick}
    >
      {inProgress ? "Saving..." : "Test against Benchmark"}
    </Button>
  );
}

export const STEP_CONFIGS: MultiStepFormV2Config<ProposalFormValues>[] = [
  {
    id: "init",
    label: (proposal) => (proposal?.id ? proposal?.name : "Name your proposal"),
    component: BasicInfo,
    show() {
      return true;
    },
    isNavigable() {
      return true;
    },
    isComplete() {
      return true;
    },
  },
  {
    id: "setup",
    label: () => "Investment Amount",
    component: ProposalSetup,
    show() {
      return true;
    },
    isNavigable(proposal) {
      return !!proposal?.id;
    },
    isComplete(proposal) {
      return !!proposal.id;
    },
  },
  {
    id: "schemes",
    label: () => "Pick Schemes",
    show() {
      return true;
    },
    isNavigable(proposal) {
      return (
        !!proposal.id &&
        (Number(proposal.total_lumpsum_amount) > 0 ||
          Number(proposal.total_sip_amount) > 0)
      );
    },
    isComplete(proposal) {
      return isFundAllocationComplete(proposal);
    },
    subSteps: [
      {
        id: "lumpsum",
        label: () => "One-time",
        subText: (proposal) => {
          const lumpsumFunds = proposal.proposal_funds
            .filter((f) => f.type === "LUMPSUM")
            .filter((f) => !!f.amc_code)
            .map((f) => ({
              amc_code: f.amc_code as string,
            }));
          return (
            <div className="h-7">
              <AmcLogoGroup size="xs" items={lumpsumFunds} />
            </div>
          );
        },
        component: () => <FundSelection type="LUMPSUM" />,
        show(proposal) {
          return Number(proposal.total_lumpsum_amount) > 0;
        },
        isNavigable(proposal) {
          return (
            proposal?.proposal_funds.filter(
              (f) => f.type === "LUMPSUM" && f.fund_scheme > 0
            ).length > 0
          );
        },
        isComplete(proposal) {
          const configuredLumpsum = proposal.proposal_funds
            .filter(
              (f) =>
                f.type === "LUMPSUM" && !Number.isNaN(Number(f.lumpsum_amount))
            )
            .reduce((sum, f) => sum + Number(f.lumpsum_amount), 0);
          return (
            Math.round(
              (Number(configuredLumpsum) /
                Number(proposal.total_lumpsum_amount)) *
                100
            ) === 100
          );
        },
      },
      {
        id: "sip",
        label: () => "Monthly SIP",
        component: () => <FundSelection type="SIP" />,
        show(proposal) {
          return Number(proposal.total_sip_amount) > 0;
        },
        subText: (proposal) => {
          const lumpsumFunds = proposal.proposal_funds
            .filter((f) => f.type === "SIP")
            .filter((f) => !!f.amc_code)
            .map((f) => ({
              amc_code: f.amc_code as string,
            }));
          return (
            <div className="h-7">
              <AmcLogoGroup size="xs" items={lumpsumFunds} />
            </div>
          );
        },
        isNavigable(proposal) {
          return (
            proposal?.proposal_funds.filter(
              (f) => f.type === "SIP" && f.fund_scheme > 0
            ).length > 0
          );
        },
        isComplete(proposal) {
          const configuredSip = proposal.proposal_funds
            .filter(
              (f) => f.type === "SIP" && !Number.isNaN(Number(f.sip_amount))
            )
            .reduce((sum, f) => sum + Number(f.sip_amount), 0);
          return (
            Math.round(
              (Number(configuredSip) / Number(proposal.total_sip_amount)) * 100
            ) === 100
          );
        },
      },
    ],
    additionalInfo: (proposal: ProposalFormValues) => (
      <BacktestButton proposal={proposal} />
    ),
  },
  {
    id: "remarks",
    label: () => "Add Remarks",
    component: CustomizeRemarks,
    show() {
      return true;
    },
    isNavigable: (proposal) => {
      const funds = proposal?.proposal_funds.filter((f) => !!f.id);
      if (!funds?.length) return false;
      if (
        Number(proposal.total_lumpsum_amount) > 0 &&
        !funds.some((f) => f.type === "LUMPSUM")
      ) {
        return false;
      }
      if (
        Number(proposal.total_sip_amount) > 0 &&
        !funds.some((f) => f.type === "SIP")
      ) {
        return false;
      }
      return true;
    },
    isComplete(proposal) {
      if (proposal?.include_remarks === false) return true;
      if (proposal?.proposal_funds?.length === 0) return false;
      return proposal?.proposal_funds?.every((f) => !!f.remarks);
    },
  },
  {
    id: "download",
    label: () => "Generate Report",
    component: DownloadProposal,
    show() {
      return false;
    },
    isNavigable() {
      return false;
    },
  },
];

export function findStepConfig(id: string) {
  // eslint-disable-next-line
  for (const c of STEP_CONFIGS) {
    if (c.id === id) return c;
    const sub = c.subSteps?.find((s) => s.id === id);
    if (sub) return sub;
  }
  return undefined;
}

export function calculateWeightedAverages(
  funds: ProposalFormValues["proposal_funds"],
  type: ProposalFund["type"]
) {
  const periods = ["return_1_year", "return_3_year", "return_5_year"] as const;
  const labels = ["1Y", "3Y", "5Y"];

  const averages = periods.map((key) => {
    const { weightedSum, validWeight } = funds.reduce(
      (acc, f) => {
        const perf = (f.fund_detail as Fund)?.performance;
        const ret = perf?.[key];
        if (ret && !Number.isNaN(Number(ret))) {
          const amount = Number(
            type === "SIP" ? f.sip_amount : f.lumpsum_amount
          );
          return {
            weightedSum: acc.weightedSum + Number(ret) * amount,
            validWeight: acc.validWeight + amount,
          };
        }
        return acc;
      },
      { weightedSum: 0, validWeight: 0 }
    );
    return validWeight > 0 ? weightedSum / validWeight : null;
  });

  if (averages.every((a) => a === null)) return null;

  return averages.map((avg, i) => ({
    label: labels[i],
    value: avg,
  }));
}
